import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Calendar, CheckCircle, AlertCircle, Users, Clock,
    DollarSign, TrendingUp, XCircle, ChevronDown, ChevronUp,
    FileText, Download, Eye, Lock, Unlock, AlertTriangle,
    CheckSquare, Square, Edit, Save, X, ArrowLeft, Search,
    Filter, ChevronLeft, ChevronRight, MoreVertical
} from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useEmployees } from '../../../contexts/EmployeeContext';
import { useAttendance } from '../../../contexts/AttendanceContext';
import functionalStructureService from '../../../services/functionalStructure.service';
import companySettingsService from '../../../services/companySettings.service';
import domainService from '../../../services/domain.service';
import leaveService from '../../../services/leave.service';
import attendanceService from '../../../services/attendance.service';
import holidayPlanService from '../../../services/holidayPlan.service';
import Payslip from './Payslip';
import AttendanceValidationWizard from './AttendanceValidationWizard';
import PAYROLL_CONFIG, { getProfessionalTax, calculatePF } from '../../../config/payrollConfig';
import professionalTaxService from '../../../services/professionalTax.service';
import payrollService from '../../../services/payroll.service';
import exitService from '../../../services/exit.service';

const PayrollRun = () => {
    const { theme } = useTheme();
    const { employees } = useEmployees();
    const { attendanceRecords, getEmployeeAttendance, getMonthlyStats } = useAttendance();
    const navigate = useNavigate();
    const [selectedMonth, setSelectedMonth] = useState('Oct 2025');
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [expandedValidation, setExpandedValidation] = useState(null);
    const [currentStep, setCurrentStep] = useState('validation');
    const [showPayslipModal, setShowPayslipModal] = useState(false);
    const [showPayslipDetail, setShowPayslipDetail] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [showValidationDetail, setShowValidationDetail] = useState(false);
    const [selectedValidation, setSelectedValidation] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [filterStatus, setFilterStatus] = useState('all');
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [showPrerequisiteModal, setShowPrerequisiteModal] = useState(false);
    const [prerequisiteIssues, setPrerequisiteIssues] = useState([]);
    const [domainRegistrationDate, setDomainRegistrationDate] = useState(null);
    const [domainInfo, setDomainInfo] = useState(null);
    const [allEmployeesAttendance, setAllEmployeesAttendance] = useState({}); // Store attendance for all employees by month
    const [employeeHolidayPlans, setEmployeeHolidayPlans] = useState({}); // Store holiday plans by employee ID
    const itemsPerPage = 10;

    // Multi-step validation wizard state (for attendance validation)
    const [validationStep, setValidationStep] = useState(1); // 1: Leaves, 2: LOP, 3: Summary
    const [confirmedLeaves, setConfirmedLeaves] = useState([]); // Employees with confirmed leaves
    const [confirmedLOP, setConfirmedLOP] = useState([]); // Employees with confirmed LOP
    const [leaveRequests, setLeaveRequests] = useState([]); // All leave requests from backend

    // Pro-ration preferences for joiners/exits (key: employeeId, value: 'prorated' or 'full_month')
    const [prorationPreferences, setProrationPreferences] = useState({});

    // Exit date overrides for HR to adjust last working day during payroll validation
    const [exitDateOverrides, setExitDateOverrides] = useState({});

    // Exit requests data for employees on notice period
    const [exitRequests, setExitRequests] = useState({});

    // Handler for HR to override exit date during payroll validation
    const handleExitDateOverride = (employeeId, newDate) => {
        setExitDateOverrides(prev => ({
            ...prev,
            [employeeId]: newDate
        }));
    };

    // Payroll periods - will be generated dynamically based on domain registration date
    const [payrollPeriods, setPayrollPeriods] = useState([]);

    // Generate payroll periods from domain registration date to current date + 2 months
    const generatePayrollPeriods = (registrationDate) => {
        const periods = [];
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthDays = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]; // Simplified, not accounting for leap years in display

        // Start from domain registration month
        const startDate = new Date(registrationDate);
        const startYear = startDate.getFullYear();
        const startMonth = startDate.getMonth();

        // End at current month + 2 months ahead
        const currentDate = new Date();
        const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 1);

        // Current month for determining status
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();

        // Iterate from start to end date
        let iterDate = new Date(startYear, startMonth, 1);

        while (iterDate <= endDate) {
            const year = iterDate.getFullYear();
            const month = iterDate.getMonth();
            const monthName = monthNames[month];
            const days = monthDays[month];

            // Determine status
            let status = 'upcoming';
            let processed = false;
            let locked = false;

            if (year < currentYear || (year === currentYear && month < currentMonth)) {
                // Past months are locked and processed
                status = 'locked';
                processed = true;
                locked = true;
            } else if (year === currentYear && month === currentMonth) {
                // Current month
                status = 'current';
                processed = false;
                locked = false;
            }

            periods.push({
                month: `${monthName} ${year}`,
                range: `${monthName.toUpperCase()} 01-${monthName.toUpperCase()} ${days}`,
                status,
                processed,
                locked,
                payslipsGenerated: processed,
                totalAmount: 0
            });

            // Move to next month
            iterDate.setMonth(iterDate.getMonth() + 1);
        }

        return periods;
    };

    // Get month/year from selected period
    const getMonthYearFromPeriod = (monthStr) => {
        const [monthName, year] = monthStr.split(' ');
        const monthMap = {
            'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
            'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
        };
        return { month: monthMap[monthName], year: parseInt(year) };
    };

    // Helper: Get holidays from holiday plan for a specific month
    const getHolidaysForMonth = (holidays, month, year) => {
        if (!holidays || holidays.length === 0) return [];

        return holidays.filter(holiday => {
            const holidayDate = new Date(holiday.date);
            return holidayDate.getMonth() === month && holidayDate.getFullYear() === year;
        });
    };

    // Helper: Count weekly offs in a month based on weekly off policy
    const countWeeklyOffs = (weeklyOffPolicy, month, year, startDate = null, endDate = null) => {
        const monthStart = startDate || new Date(year, month, 1);
        const monthEnd = endDate || new Date(year, month + 1, 0);

        let weeklyOffCount = 0;
        let currentDate = new Date(monthStart);

        while (currentDate <= monthEnd) {
            const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 6 = Saturday

            if (weeklyOffPolicy === 'Sunday Only') {
                if (dayOfWeek === 0) weeklyOffCount++;
            } else if (weeklyOffPolicy === 'Saturday & Sunday') {
                if (dayOfWeek === 0 || dayOfWeek === 6) weeklyOffCount++;
            } else if (weeklyOffPolicy === 'Standard Weekly Off Policy') {
                // Standard: Sunday + alternate Saturdays (1.5 days/week avg)
                if (dayOfWeek === 0) weeklyOffCount++;
                // Alternate Saturdays: Check if it's 1st or 3rd Saturday
                if (dayOfWeek === 6) {
                    const dateNum = currentDate.getDate();
                    const weekOfMonth = Math.ceil(dateNum / 7);
                    if (weekOfMonth === 2 || weekOfMonth === 4) {
                        weeklyOffCount++;
                    }
                }
            }

            currentDate.setDate(currentDate.getDate() + 1);
        }

        return weeklyOffCount;
    };

    // Helper: Calculate actual working days for a month excluding holidays and weekly offs
    const calculateWorkingDays = (month, year, holidays, weeklyOffPolicy, startDate = null, endDate = null) => {
        const monthStart = startDate || new Date(year, month, 1);
        const monthEnd = endDate || new Date(year, month + 1, 0);

        // Total days in the period
        const totalDays = Math.floor((monthEnd - monthStart) / (1000 * 60 * 60 * 24)) + 1;

        // Get holidays for this month
        const monthHolidays = getHolidaysForMonth(holidays, month, year);

        // Count holidays that fall within the date range and are not on weekly offs
        let holidayCount = 0;
        monthHolidays.forEach(holiday => {
            const holidayDate = new Date(holiday.date);
            if (holidayDate >= monthStart && holidayDate <= monthEnd) {
                const dayOfWeek = holidayDate.getDay();
                let isWeeklyOff = false;

                if (weeklyOffPolicy === 'Sunday Only' && dayOfWeek === 0) {
                    isWeeklyOff = true;
                } else if (weeklyOffPolicy === 'Saturday & Sunday' && (dayOfWeek === 0 || dayOfWeek === 6)) {
                    isWeeklyOff = true;
                } else if (weeklyOffPolicy === 'Standard Weekly Off Policy') {
                    if (dayOfWeek === 0) isWeeklyOff = true;
                    if (dayOfWeek === 6) {
                        const dateNum = holidayDate.getDate();
                        const weekOfMonth = Math.ceil(dateNum / 7);
                        if (weekOfMonth === 2 || weekOfMonth === 4) isWeeklyOff = true;
                    }
                }

                // Only count holiday if it's not already a weekly off
                if (!isWeeklyOff) {
                    holidayCount++;
                }
            }
        });

        // Count weekly offs in the period
        const weeklyOffCount = countWeeklyOffs(weeklyOffPolicy, month, year, monthStart, monthEnd);

        // Working days = Total days - Holidays - Weekly offs
        const workingDays = totalDays - holidayCount - weeklyOffCount;

        return {
            totalDays,
            holidayCount,
            weeklyOffCount,
            workingDays: Math.max(0, workingDays)
        };
    };

    // Process real employee data with attendance and leaves
    const processEmployeeData = (employees, category) => {
        const { month, year } = getMonthYearFromPeriod(selectedMonth);

        return employees.map(emp => {
            // Check for mid-month joining or exit
            const joiningDate = emp.joining_date || emp.joiningDate ? new Date(emp.joining_date || emp.joiningDate) : null;

            // Get exit date from multiple sources (priority order):
            // 1. HR override for this payroll run
            // 2. Employee record's exit_date (set when approved)
            // 3. Exit request's effective date (approved or requested)
            const exitRequest = exitRequests[emp.id];
            const originalExitDate = emp.exit_date || emp.exitDate || emp.last_working_day;
            const exitRequestDate = exitRequest?.effectiveExitDate;
            const exitDateValue = exitDateOverrides[emp.id] || originalExitDate || exitRequestDate;
            const exitDate = exitDateValue ? new Date(exitDateValue) : null;

            // Track if using override for UI indication
            const isExitDateOverridden = exitDateOverrides[emp.id] && exitDateOverrides[emp.id] !== (originalExitDate || exitRequestDate);

            // Track exit request status for UI
            const exitRequestStatus = exitRequest?.status;

            const monthStart = new Date(year, month, 1);
            const monthEnd = new Date(year, month + 1, 0);
            const totalDaysInMonth = monthEnd.getDate();

            // Determine effective working period for this month
            let effectiveStartDate = monthStart;
            let effectiveEndDate = monthEnd;
            let isProrated = false;
            let joinerType = null; // 'joiner', 'exit', or null

            if (joiningDate && joiningDate.getMonth() === month && joiningDate.getFullYear() === year) {
                effectiveStartDate = joiningDate;
                isProrated = true;
                joinerType = 'joiner';
            }

            if (exitDate && exitDate.getMonth() === month && exitDate.getFullYear() === year) {
                effectiveEndDate = exitDate;
                isProrated = true;
                joinerType = joinerType === 'joiner' ? 'joiner-exit' : 'exit';

                // Store additional info for validation UI
                emp.calculatedLastWorkingDay = exitDate;
                emp.originalExitDate = originalExitDate;
                emp.isExitDateOverridden = isExitDateOverridden;
            }

            // Get employee's holiday plan and weekly off configuration
            const employeeHolidayPlan = employeeHolidayPlans[emp.id];
            const holidays = employeeHolidayPlan?.holidays || [];
            const weeklyOffPolicy = emp.weekly_off || emp.weeklyOff || 'Standard Weekly Off Policy';

            // Calculate actual working days for this employee based on:
            // 1. Holiday plan (excluding holidays)
            // 2. Weekly off policy (Sunday only / Saturday & Sunday / Standard)
            // 3. Joining/Exit dates (for pro-ration)
            const workingDaysData = calculateWorkingDays(
                month,
                year,
                holidays,
                weeklyOffPolicy,
                effectiveStartDate,
                effectiveEndDate
            );

            // Calculate pro-rated working days
            const effectiveDays = Math.floor((effectiveEndDate - effectiveStartDate) / (1000 * 60 * 60 * 24)) + 1;

            // Check if HR has set a pro-ration preference for this employee
            const prorationPref = prorationPreferences[emp.id] || (isProrated ? 'pending' : 'full_month');
            const shouldProrate = prorationPref === 'prorated';

            // Use calculated working days (excluding holidays and weekly offs)
            // For pro-rated employees, we already calculated for their effective period
            const proratedWorkingDays = workingDaysData.workingDays;

            console.log(`\n👤 Employee: ${emp.name || emp.first_name} (${emp.id})`);
            console.log(`📅 Period: ${effectiveStartDate.toLocaleDateString()} to ${effectiveEndDate.toLocaleDateString()}`);
            console.log(`📊 Working Days Breakdown:`);
            console.log(`   - Total Days: ${workingDaysData.totalDays}`);
            console.log(`   - Holidays: ${workingDaysData.holidayCount}`);
            console.log(`   - Weekly Offs (${weeklyOffPolicy}): ${workingDaysData.weeklyOffCount}`);
            console.log(`   - Working Days: ${proratedWorkingDays}`);
            if (isProrated) {
                console.log(`   ⚠️ ${joinerType === 'joiner' ? 'New Joiner' : 'Exit'}: Calculating from ${joinerType === 'joiner' ? 'joining' : 'exit'} date`);
            }

            // Get attendance for this employee for selected month from allEmployeesAttendance
            const attendanceRecordsList = allEmployeesAttendance[emp.id] || [];

            // Calculate monthly stats from attendance records
            const monthlyStats = {
                present: attendanceRecordsList.filter(r => r.status === 'present').length,
                absent: attendanceRecordsList.filter(r => r.status === 'absent').length,
                halfDay: attendanceRecordsList.filter(r => r.status === 'half-day').length,
                leave: attendanceRecordsList.filter(r => r.status === 'leave').length,
                totalWorkingHours: attendanceRecordsList.reduce((sum, r) => sum + parseFloat(r.workingHours || 0), 0)
            };

            // Get approved leave requests for this employee for the selected month
            const empLeaveRequests = leaveRequests.filter(request => {
                if (request.employee_id !== emp.id && request.employee_id !== emp.employee_code) {
                    return false;
                }
                if (request.status !== 'approved') {
                    return false;
                }

                // Check if leave request overlaps with selected month
                const fromDate = new Date(request.from_date);
                const toDate = new Date(request.to_date);
                const monthStart = new Date(year, month, 1);
                const monthEnd = new Date(year, month + 1, 0);

                return !(toDate < monthStart || fromDate > monthEnd);
            });

            // Calculate approved leave days that fall within the selected month
            let approvedLeaveDays = 0;
            empLeaveRequests.forEach(request => {
                const fromDate = new Date(request.from_date);
                const toDate = new Date(request.to_date);
                const monthStart = new Date(year, month, 1);
                const monthEnd = new Date(year, month + 1, 0);

                // Get the overlap between leave period and selected month
                const overlapStart = fromDate > monthStart ? fromDate : monthStart;
                const overlapEnd = toDate < monthEnd ? toDate : monthEnd;

                // Calculate days (inclusive)
                const daysDiff = Math.floor((overlapEnd - overlapStart) / (1000 * 60 * 60 * 24)) + 1;
                approvedLeaveDays += Math.max(0, daysDiff);
            });

            // Calculate partial days (attendance < 8 hours)
            const partialDays = attendanceRecordsList.filter(record => {
                if (record.status !== 'present') return false;
                const hours = parseFloat(record.workingHours || 0);
                return hours > 0 && hours < 8;
            });

            const fullDays = attendanceRecordsList.filter(record => {
                if (record.status !== 'present') return false;
                const hours = parseFloat(record.workingHours || 0);
                return hours >= 8;
            });

            // Use pro-rated working days if employee joined/exited mid-month
            const workingDays = proratedWorkingDays;
            const presentFullDays = fullDays.length;
            const presentPartialDays = partialDays.length;
            const presentDays = monthlyStats?.present || 0; // Total present days

            // CRITICAL: LOP Calculation Logic
            // LOP (Loss of Pay) = Days where employee was neither present NOR on approved leave
            // Formula: LOP = Working Days - Present Days - Approved Leave Days
            //
            // Example 1: Employee worked all days
            //   Working Days: 22, Present: 22, Leaves: 0 → LOP = 22 - 22 - 0 = 0
            //
            // Example 2: Employee took 2 approved leaves
            //   Working Days: 22, Present: 20, Leaves: 2 → LOP = 22 - 20 - 2 = 0 (No deduction!)
            //
            // Example 3: Employee took 2 unapproved absences
            //   Working Days: 22, Present: 20, Leaves: 0 → LOP = 22 - 20 - 0 = 2 (Salary deducted!)
            //
            // Example 4: Employee took 2 approved leaves + 1 unapproved absence
            //   Working Days: 22, Present: 19, Leaves: 2 → LOP = 22 - 19 - 2 = 1 (1 day deducted)
            const lop = Math.max(0, workingDays - presentDays - approvedLeaveDays);

            // if (presentPartialDays > 0) {
            //     console.log(`⚠️ Employee ${emp.id} (${emp.name || emp.first_name}) has ${presentPartialDays} PARTIAL DAYS:`, partialDays.map(d => `${d.date}: ${d.workingHours}h`));
            // }
            // console.log(`📊 Employee ${emp.id}: WD=${workingDays}, Present=${presentDays} (Full:${presentFullDays}, Partial:${presentPartialDays}), Leaves=${approvedLeaveDays}, LOP=${lop}`);
            // console.log(`   💰 Salary Calculation: Payable Days = ${workingDays} - ${lop} = ${workingDays - lop} days`);

            // Get salary info - Pro-rate if mid-month joiner/exit and HR chose pro-ration
            const ctc = parseFloat(emp.ctc || 0);
            const monthlyCTC = ctc / 12;
            let basicSalary = monthlyCTC * 0.5; // 50% of CTC as basic

            // If pro-rated AND preference is 'prorated', adjust the salary accordingly
            if (shouldProrate) {
                const prorationFactor = workingDays / 22; // workingDays is already pro-rated
                basicSalary = basicSalary * prorationFactor;
                // console.log(`💰 Employee ${emp.id}: Pro-rated salary ${prorationFactor.toFixed(2)}x = ₹${basicSalary.toFixed(2)}`);
            } else if (isProrated && prorationPref === 'full_month') {
                // console.log(`💰 Employee ${emp.id}: Full month salary (no pro-ration) = ₹${basicSalary.toFixed(2)}`);
            }

            // Determine status based on validation requirements
            let status = 'validated';
            if (category === 'attendance') {
                // Mark as issue if LOP > 0 or partial days exist or attendance incomplete
                if (lop > 0 || presentPartialDays > 0 || presentDays + approvedLeaveDays < workingDays) {
                    status = 'issue';
                } else if (presentDays === 0 && approvedLeaveDays === 0) {
                    status = 'pending';
                }
            } else if (category === 'joiners') {
                // All joiners/exits should be marked as pending for HR confirmation
                if (isProrated) {
                    status = 'pending';
                } else {
                    status = 'validated';
                }
            } else {
                status = Math.random() > 0.7 ? 'pending' : 'validated';
            }

            return {
                id: emp.id || emp.employee_code,
                name: emp.name || `${emp.first_name} ${emp.last_name}`,
                status,
                workingDays,
                lop,
                leaves: approvedLeaveDays, // Now correctly showing approved leave days from leave_requests
                overtime: 0, // Can be calculated from attendance if overtime tracking is enabled
                basicSalary,
                department: emp.department,
                employee: emp,
                partialDays: partialDays, // Days with < 8 hours
                partialDaysCount: presentPartialDays,
                // Pro-ration metadata
                isProrated: isProrated,
                joinerType: joinerType,
                joiningDate: joiningDate,
                exitDate: exitDate,
                originalExitDate: originalExitDate ? new Date(originalExitDate) : (exitRequestDate ? new Date(exitRequestDate) : null),
                isExitDateOverridden: isExitDateOverridden,
                exitRequestStatus: exitRequestStatus, // 'approved', 'pending_approval', etc.
                effectiveStartDate: effectiveStartDate,
                effectiveEndDate: effectiveEndDate,
                effectiveDays: effectiveDays,
                prorationPreference: prorationPref, // 'prorated', 'full_month', or 'pending'
                shouldProrate: shouldProrate,
                // Working days breakdown (includes holidays and weekly offs)
                totalDaysInPeriod: workingDaysData.totalDays,
                holidayCount: workingDaysData.holidayCount,
                weeklyOffCount: workingDaysData.weeklyOffCount,
                weeklyOffPolicy: weeklyOffPolicy
            };
        });
    };

    // Filter employees based on joining/exit for current month
    // Shows exits from both approved employee records and exit_requests table
    const getJoinersAndExits = () => {
        const { month, year } = getMonthYearFromPeriod(selectedMonth);

        return employees.filter(emp => {
            const joiningDate = new Date(emp.joining_date || emp.joiningDate);

            // Get exit date from multiple sources (priority order):
            // 1. HR override for this payroll run
            // 2. Employee record's exit_date (set when approved)
            // 3. Exit request's effective date (approved or requested)
            const exitRequest = exitRequests[emp.id];
            const originalExitDate = emp.exit_date || emp.exitDate || emp.last_working_day;
            const exitRequestDate = exitRequest?.effectiveExitDate;
            const exitDateValue = exitDateOverrides[emp.id] || originalExitDate || exitRequestDate;
            const exitDate = exitDateValue ? new Date(exitDateValue) : null;

            const joinedThisMonth = joiningDate.getMonth() === month && joiningDate.getFullYear() === year;

            // Include exits where:
            // 1. Exit date exists (from employee record OR exit request)
            // 2. Exit date falls within the payroll month
            // 3. Employee has exit status OR has an exit request
            const validExitStatuses = ['notice_period', 'resigned', 'exited', 'terminated'];
            const hasExitData = exitDate && (validExitStatuses.includes(emp.employment_status) || originalExitDate || exitRequest);
            const exitedThisMonth = hasExitData && exitDate.getMonth() === month && exitDate.getFullYear() === year;

            return joinedThisMonth || exitedThisMonth;
        });
    };

    // Get employees with salary revisions (can be extended to track actual revisions)
    const getRevisionsAndBonus = () => {
        // This will be populated when revision tracking is implemented
        // For now, return empty array
        return [];
    };

    // Get employees with pending payments/reimbursements
    const getPendingPayments = () => {
        // This will be populated when reimbursement/payment module is added
        // For now, return empty array
        return [];
    };

    // Get employees on hold
    const getOnHoldEmployees = () => {
        return employees.filter(emp => emp.employment_status === 'hold' || emp.payroll_hold);
    };

    // Get employees with overrides
    const getOverrideEmployees = () => {
        // This will be populated when override tracking is implemented
        // For now, return empty array
        return [];
    };

    // Validation categories with real data
    const [validations, setValidations] = useState([]);

    // Fetch domain information on component mount
    useEffect(() => {
        const fetchDomainInfo = async () => {
            try {
                // console.log('🔍 Fetching domain information for payroll security...');

                // SECURITY: This API call should only return the current user's domain data
                // Backend must verify authentication token and return only authorized domain
                const domainData = await domainService.getDomainInfo();
                // console.log('✅ Domain info fetched:', domainData);

                setDomainInfo(domainData);

                if (domainData && (domainData.created_at || domainData.registration_date)) {
                    const regDate = new Date(domainData.created_at || domainData.registration_date);
                    setDomainRegistrationDate(regDate);
                    // console.log('📅 Company registration date (created_at):', regDate.toLocaleDateString());

                    // Generate payroll periods from registration date
                    const periods = generatePayrollPeriods(regDate);
                    setPayrollPeriods(periods);
                    // console.log(`📊 Generated ${periods.length} payroll periods from registration date`);

                    // Set current month as selected by default
                    const currentPeriod = periods.find(p => p.status === 'current');
                    if (currentPeriod && !selectedMonth) {
                        setSelectedMonth(currentPeriod.month);
                    } else if (periods.length > 0 && !selectedMonth) {
                        // If no current month, select the latest period
                        setSelectedMonth(periods[periods.length - 1].month);
                    }
                } else {
                    // console.warn('⚠️ No domain registration date found, using default');
                    // Fallback to default date if not found
                    const defaultDate = new Date('2024-04-01');
                    setDomainRegistrationDate(defaultDate);
                    const periods = generatePayrollPeriods(defaultDate);
                    setPayrollPeriods(periods);
                    const currentPeriod = periods.find(p => p.status === 'current');
                    if (currentPeriod && !selectedMonth) {
                        setSelectedMonth(currentPeriod.month);
                    }
                }
            } catch (error) {
                console.error('❌ Error fetching domain info:', error);
                // Fallback to default date on error
                const defaultDate = new Date('2024-04-01');
                setDomainRegistrationDate(defaultDate);
                const periods = generatePayrollPeriods(defaultDate);
                setPayrollPeriods(periods);
                const currentPeriod = periods.find(p => p.status === 'current');
                if (currentPeriod && !selectedMonth) {
                    setSelectedMonth(currentPeriod.month);
                }
            }
        };

        fetchDomainInfo();
    }, []); // Run once on mount

    // Fetch leave requests when selected month changes
    useEffect(() => {
        const fetchLeaveRequests = async () => {
            if (!selectedMonth) return;

            try {
                // console.log('🔍 Fetching leave requests for month:', selectedMonth);
                const response = await leaveService.getAllLeaveRequests();
                const requests = response.requests || [];
                // console.log('✅ Fetched leave requests:', requests.length);
                setLeaveRequests(requests);
            } catch (error) {
                console.error('❌ Error fetching leave requests:', error);
                setLeaveRequests([]);
            }
        };

        fetchLeaveRequests();
    }, [selectedMonth]);

    // Fetch exit requests for employees on notice period
    useEffect(() => {
        const fetchExitRequests = async () => {
            if (!selectedMonth || !employees || employees.length === 0) return;

            try {
                const { month, year } = getMonthYearFromPeriod(selectedMonth);

                // Find employees on notice period or with pending/approved exit requests
                const noticePeriodEmployees = employees.filter(emp =>
                    emp.employment_status === 'notice_period' ||
                    emp.exit_initiated_at
                );

                if (noticePeriodEmployees.length === 0) {
                    setExitRequests({});
                    return;
                }

                // Fetch all approved and pending exit requests
                const response = await exitService.getAllExits({ status: 'approved' });
                const pendingResponse = await exitService.getAllExits({ status: 'pending_approval' });

                const allExitRequests = [
                    ...(response.data || []),
                    ...(pendingResponse.data || [])
                ];

                // Create a map of employee_id to exit request
                const exitRequestsMap = {};
                allExitRequests.forEach(exitReq => {
                    // Check if the exit date falls within the payroll month
                    const exitDate = new Date(exitReq.approved_exit_date || exitReq.requested_exit_date);
                    if (exitDate.getMonth() === month && exitDate.getFullYear() === year) {
                        exitRequestsMap[exitReq.employee_id] = {
                            id: exitReq.id,
                            status: exitReq.status,
                            requestedExitDate: exitReq.requested_exit_date,
                            approvedExitDate: exitReq.approved_exit_date,
                            effectiveExitDate: exitReq.approved_exit_date || exitReq.requested_exit_date,
                            resignationDate: exitReq.resignation_date,
                            noticePeriodDays: exitReq.notice_period_days,
                            exitType: exitReq.exit_type,
                            exitReason: exitReq.exit_reason
                        };
                    }
                });

                console.log('📤 Exit requests for payroll month:', Object.keys(exitRequestsMap).length);
                setExitRequests(exitRequestsMap);
            } catch (error) {
                console.error('❌ Error fetching exit requests:', error);
                setExitRequests({});
            }
        };

        fetchExitRequests();
    }, [selectedMonth, employees]);

    // Fetch attendance for all employees when selected month changes
    useEffect(() => {
        const fetchAllEmployeesAttendance = async () => {
            if (!selectedMonth || !employees || employees.length === 0) return;

            try {
                const { month, year } = getMonthYearFromPeriod(selectedMonth);
                // console.log('🔍 Fetching attendance for all employees for:', selectedMonth, `(month: ${month + 1}, year: ${year})`);

                const attendanceData = {};

                // Fetch attendance for each employee
                const attendancePromises = employees.map(async (emp) => {
                    try {
                        const response = await attendanceService.getEmployeeAttendance(emp.id, month + 1, year);
                        if (response && response.monthly) {
                            attendanceData[emp.id] = response.monthly.map(record => ({
                                date: record.date.split('T')[0],
                                status: record.status,
                                workingHours: record.work_hours || record.working_hours || 0,
                                check_in_time: record.check_in_time,
                                check_out_time: record.check_out_time
                            }));
                        }
                    } catch (error) {
                        console.error(`Error fetching attendance for employee ${emp.id}:`, error);
                        attendanceData[emp.id] = [];
                    }
                });

                await Promise.all(attendancePromises);

                // console.log('✅ Fetched attendance for all employees. Sample:', Object.keys(attendanceData).length, 'employees');
                setAllEmployeesAttendance(attendanceData);
            } catch (error) {
                console.error('❌ Error fetching all employees attendance:', error);
            }
        };

        fetchAllEmployeesAttendance();
    }, [selectedMonth, employees]);

    // Fetch payroll status from backend when selectedMonth or payrollPeriods change
    useEffect(() => {
        const fetchPayrollStatus = async () => {
            if (!selectedMonth || payrollPeriods.length === 0) return;

            try {
                console.log('🔍 Fetching payroll status for:', selectedMonth);
                const statusResponse = await payrollService.getPayrollStatus(selectedMonth);

                if (statusResponse && statusResponse.status) {
                    const { processed, locked, status, payslipsGenerated } = statusResponse.status;

                    console.log('✅ Payroll status from backend:', statusResponse.status);

                    // Update the payroll periods with the actual status from backend
                    setPayrollPeriods(prev => prev.map(period => {
                        if (period.month === selectedMonth) {
                            return {
                                ...period,
                                processed: processed || false,
                                locked: locked || false,
                                status: locked ? 'locked' : (processed ? 'completed' : period.status),
                                payslipsGenerated: payslipsGenerated || false
                            };
                        }
                        return period;
                    }));
                }
            } catch (error) {
                console.error('❌ Error fetching payroll status:', error);
            }
        };

        fetchPayrollStatus();
    }, [selectedMonth, payrollPeriods.length]); // Only re-run when selectedMonth changes or periods are loaded

    // Fetch holiday plans for all employees
    useEffect(() => {
        const fetchEmployeeHolidayPlans = async () => {
            if (!employees || employees.length === 0) return;

            try {
                const holidayPlansData = {};

                // Fetch unique holiday plans
                const uniqueHolidayPlanIds = [...new Set(
                    employees
                        .map(emp => emp.holiday_plan_id || emp.holiday_list || emp.holidayList)
                        .filter(Boolean)
                )];

                // Fetch all unique holiday plans in parallel
                const planPromises = uniqueHolidayPlanIds.map(async (planId) => {
                    try {
                        const plan = await holidayPlanService.getHolidayPlanById(planId);
                        return { planId, plan };
                    } catch (error) {
                        console.error(`Error fetching holiday plan ${planId}:`, error);
                        return { planId, plan: null };
                    }
                });

                const planResults = await Promise.all(planPromises);

                // Create a map of planId to plan data
                const planMap = {};
                planResults.forEach(({ planId, plan }) => {
                    if (plan) planMap[planId] = plan;
                });

                // Assign plans to employees
                employees.forEach(emp => {
                    const planId = emp.holiday_plan_id || emp.holiday_list || emp.holidayList;
                    if (planId && planMap[planId]) {
                        holidayPlansData[emp.id] = planMap[planId];
                    }
                });

                setEmployeeHolidayPlans(holidayPlansData);
            } catch (error) {
                console.error('Error fetching employee holiday plans:', error);
            }
        };

        fetchEmployeeHolidayPlans();
    }, [employees]);

    useEffect(() => {
        // Update validations when employees, selected month, or leave requests change
        const attendanceEmployees = processEmployeeData(employees, 'attendance');
        const joinersExits = processEmployeeData(getJoinersAndExits(), 'joiners');
        const revisions = processEmployeeData(getRevisionsAndBonus(), 'revisions');
        const payments = processEmployeeData(getPendingPayments(), 'payments');
        const holds = processEmployeeData(getOnHoldEmployees(), 'holds');
        const overrides = processEmployeeData(getOverrideEmployees(), 'overrides');

        const calculateCounts = (empList) => ({
            totalEmployees: empList.length,
            validatedCount: empList.filter(e => e.status === 'validated').length,
            issuesCount: empList.filter(e => e.status === 'issue').length,
            pendingCount: empList.filter(e => e.status === 'pending').length,
        });

        setValidations([
            {
                id: 'joinersExits',
                title: 'New Joinees & Exits',
                icon: Users,
                color: 'purple',
                validated: false,
                ...calculateCounts(joinersExits),
                employees: joinersExits
            },
            {
                id: 'attendance',
                title: 'Leave, Attendance & Payable Units',
                icon: Clock,
                color: 'blue',
                validated: false,
                ...calculateCounts(attendanceEmployees),
                employees: attendanceEmployees
            },
            {
                id: 'revisions',
                title: 'Bonus, Salary Revisions & Overtime',
                icon: TrendingUp,
                color: 'pink',
                validated: false,
                ...calculateCounts(revisions),
                employees: revisions
            },
            {
                id: 'payments',
                title: 'Reimbursement, Adhoc Payment, Deduction',
                icon: DollarSign,
                color: 'cyan',
                validated: false,
                ...calculateCounts(payments),
                employees: payments
            },
            {
                id: 'holds',
                title: 'Salaries on Hold & Arrears',
                icon: AlertCircle,
                color: 'orange',
                validated: false,
                ...calculateCounts(holds),
                employees: holds
            },
            {
                id: 'overrides',
                title: 'Override (PT, ESI, TDS, LWF)',
                icon: AlertTriangle,
                color: 'yellow',
                validated: false,
                ...calculateCounts(overrides),
                employees: overrides
            },
        ]);
    }, [employees, selectedMonth, leaveRequests, allEmployeesAttendance, employeeHolidayPlans]);

    useEffect(() => {
        const period = payrollPeriods.find(p => p.month === selectedMonth);
        setSelectedPeriod(period);
    }, [selectedMonth, payrollPeriods]);

    // Check prerequisites on component mount
    useEffect(() => {
        checkPrerequisites();
    }, [employees]);

    const checkPrerequisites = async () => {
        const issues = [];

        // PRIORITY 1: Check if organization structure exists
        try {
            const structureData = await functionalStructureService.getActive();
            if (!structureData || !structureData.structure_data) {
                issues.push({
                    title: 'Organization Not Configured',
                    description: 'Please configure your organization structure before running payroll.',
                    action: 'Setup Organization',
                    route: '/admin/org-view'
                });
            } else {
                const parsed = structureData.structure_data;
                if (!parsed.legalEntities || parsed.legalEntities.length === 0) {
                    issues.push({
                        title: 'Legal Entities Not Configured',
                        description: 'Please add at least one legal entity to your organization. This is required before adding employees or running payroll.',
                        action: 'Setup Legal Entities',
                        route: '/admin/org-view'
                    });
                }
            }
        } catch (e) {
            issues.push({
                title: 'Organization Configuration Error',
                description: 'There was an error reading your organization configuration.',
                action: 'Setup Organization',
                route: '/admin/org-view'
            });
        }

        // PRIORITY 2: Check if employees exist
        if (!employees || employees.length === 0) {
            issues.push({
                title: 'No Employees Added',
                description: 'You need to add employees before running payroll.',
                action: 'Add Employees',
                route: '/admin/employees'
            });
        }

        // PRIORITY 3: Check if company settings are complete
        try {
            const settings = await companySettingsService.getSettings();
            if (!settings) {
                issues.push({
                    title: 'Company Settings Not Configured',
                    description: 'Please complete your company information including address and bank details.',
                    action: 'Complete Company Settings',
                    route: '/admin/settings'
                });
            } else {
                const isComplete = settings.name &&
                                 settings.email &&
                                 settings.address?.street &&
                                 settings.address?.city &&
                                 settings.bankDetails?.bankName &&
                                 settings.bankDetails?.accountNumber;

                if (!isComplete) {
                    issues.push({
                        title: 'Company Settings Incomplete',
                        description: 'Please complete all required fields: company name, email, address, and bank details.',
                        action: 'Complete Company Settings',
                        route: '/admin/settings'
                    });
                }
            }
        } catch (e) {
            issues.push({
                title: 'Company Settings Error',
                description: 'There was an error reading your company settings.',
                action: 'Fix Company Settings',
                route: '/admin/settings'
            });
        }

        // PRIORITY 4: Skip document templates check for now (not critical)
        // const templates = await documentTemplatesService.getSelected();
        // if (!templates || Object.keys(templates).length === 0) {
        //     issues.push(...);
        // }

        setPrerequisiteIssues(issues);
        if (issues.length > 0) {
            setShowPrerequisiteModal(true);
        }
    };

    const openValidationDetail = (validation) => {
        setSelectedValidation(validation);

        // Use wizard for attendance validation, regular modal for others
        if (validation.id === 'attendance') {
            setShowValidationDetail(true);
            // Reset wizard state
            setValidationStep(1);
            setConfirmedLeaves([]);
            setConfirmedLOP([]);
        } else {
            setShowValidationDetail(true);
            setSearchTerm('');
            setCurrentPage(1);
            setFilterStatus('all');
            setSelectedEmployees([]);
        }
    };

    const validateCategory = (id) => {
        setValidations(validations.map(v =>
            v.id === id ? {
                ...v,
                validated: true,
                validatedCount: v.totalEmployees,
                issuesCount: 0,
                pendingCount: 0,
                employees: v.employees.map(e => ({ ...e, status: 'validated' }))
            } : v
        ));
    };

    const bulkValidate = () => {
        if (selectedEmployees.length === 0) return;

        const updatedEmployees = selectedValidation.employees.map(emp =>
            selectedEmployees.includes(emp.id) ? { ...emp, status: 'validated' } : emp
        );

        const validatedCount = updatedEmployees.filter(e => e.status === 'validated').length;
        const issuesCount = updatedEmployees.filter(e => e.status === 'issue').length;
        const pendingCount = updatedEmployees.filter(e => e.status === 'pending').length;

        setValidations(validations.map(v =>
            v.id === selectedValidation.id ? {
                ...v,
                employees: updatedEmployees,
                validatedCount,
                issuesCount,
                pendingCount,
                validated: validatedCount === v.totalEmployees
            } : v
        ));

        setSelectedValidation({
            ...selectedValidation,
            employees: updatedEmployees,
            validatedCount,
            issuesCount,
            pendingCount
        });

        setSelectedEmployees([]);
    };

    const toggleEmployeeSelection = (id) => {
        setSelectedEmployees(prev =>
            prev.includes(id) ? prev.filter(empId => empId !== id) : [...prev, id]
        );
    };

    const selectAllFiltered = () => {
        const filtered = getFilteredEmployees();
        const allIds = filtered.map(e => e.id);
        setSelectedEmployees(allIds);
    };

    const getFilteredEmployees = () => {
        if (!selectedValidation) return [];

        let filtered = selectedValidation.employees;

        if (searchTerm) {
            filtered = filtered.filter(emp =>
                emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                emp.id.toString().includes(searchTerm)
            );
        }

        if (filterStatus !== 'all') {
            filtered = filtered.filter(emp => emp.status === filterStatus);
        }

        return filtered;
    };

    const getPaginatedEmployees = () => {
        const filtered = getFilteredEmployees();
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filtered.slice(startIndex, startIndex + itemsPerPage);
    };

    const getTotalPages = () => {
        const filtered = getFilteredEmployees();
        return Math.ceil(filtered.length / itemsPerPage);
    };

    // Get employees who took leaves (for step 1)
    const getEmployeesWithLeaves = () => {
        if (!selectedValidation || selectedValidation.id !== 'attendance') return [];
        return selectedValidation.employees.filter(emp => emp.leaves > 0);
    };

    // Get employees with LOP (for step 2)
    const getEmployeesWithLOP = () => {
        if (!selectedValidation || selectedValidation.id !== 'attendance') return [];
        return selectedValidation.employees.filter(emp => emp.lop > 0);
    };

    // Handle wizard step navigation
    const handleContinueToLOP = () => {
        setConfirmedLeaves(getEmployeesWithLeaves());
        setValidationStep(2);
        setSelectedEmployees([]);
        setCurrentPage(1);
    };

    const handleContinueToSummary = () => {
        setConfirmedLOP(getEmployeesWithLOP());
        setValidationStep(3);
        setSelectedEmployees([]);
        setCurrentPage(1);
    };

    const handleBackToLeaves = () => {
        setValidationStep(1);
        setCurrentPage(1);
    };

    const handleBackToLOP = () => {
        setValidationStep(2);
        setCurrentPage(1);
    };

    const allValidated = validations.every(v => v.validated);

    // State for processing status
    const [processingError, setProcessingError] = useState(null);
    const [processingProgress, setProcessingProgress] = useState(0);

    const processPayroll = async () => {
        if (!allValidated) {
            alert('Please complete all validations before processing payroll');
            return;
        }

        setCurrentStep('processing');
        setProcessingError(null);
        setProcessingProgress(0);

        try {
            // Get attendance-based employee data
            const attendanceEmployees = processEmployeeData(employees, 'attendance');

            // Prepare detailed breakdown for leaves and attendance
            const employeesWithLeaves = [];
            const employeesWithAttendanceIssues = [];

            // Build payroll data for all employees
            const employeePayrollData = [];
            let totalAmount = 0;

            attendanceEmployees.forEach((emp, index) => {
                const attendanceData = {
                    workingDays: emp.workingDays,
                    lop: emp.lop,
                    leaves: emp.leaves,
                    basicSalary: emp.basicSalary
                };
                const salary = calculateNetSalary(emp.employee, attendanceData);

                // Build payslip record for backend
                employeePayrollData.push({
                    employeeId: emp.employee.id || emp.employee.employee_code,
                    employeeCode: emp.employee.employee_code,
                    employeeName: emp.name,
                    department: emp.department,
                    period: selectedMonth,
                    // Attendance data
                    workingDays: emp.workingDays,
                    payableDays: salary.payableDays,
                    lop: emp.lop,
                    approvedLeaves: emp.leaves,
                    // Earnings
                    gross: Math.round(salary.gross * 100) / 100,
                    earningsBreakdown: salary.earningsBreakdown,
                    // Deductions
                    pf: Math.round(salary.pf * 100) / 100,
                    professionalTax: Math.round(salary.professionalTax * 100) / 100,
                    tds: salary.tax,
                    otherDeductions: Math.round(salary.otherDeductions * 100) / 100,
                    totalDeductions: Math.round((salary.pf + salary.professionalTax + salary.tax + salary.otherDeductions) * 100) / 100,
                    deductionsBreakdown: salary.deductionsBreakdown,
                    lopDeduction: Math.round(salary.lopDeduction * 100) / 100,
                    // Net salary
                    net: Math.round(salary.net * 100) / 100,
                    // Pro-ration info
                    isProrated: emp.isProrated || false,
                    joinerType: emp.joinerType,
                    joiningDate: emp.joiningDate,
                    exitDate: emp.exitDate
                });

                totalAmount += salary.net;

                // Track employees with leaves
                if (emp.leaves > 0) {
                    employeesWithLeaves.push({
                        name: emp.name,
                        leaves: emp.leaves,
                        netSalary: Math.round(salary.net)
                    });
                }

                // Track employees with LOP or attendance issues
                if (emp.lop > 0) {
                    employeesWithAttendanceIssues.push({
                        name: emp.name,
                        workingDays: emp.workingDays,
                        lop: emp.lop,
                        lopDeduction: Math.round(salary.lopDeduction || 0),
                        netSalary: Math.round(salary.net)
                    });
                }

                // Update progress
                setProcessingProgress(Math.round(((index + 1) / attendanceEmployees.length) * 50));
            });

            console.log('Processing payroll for period:', selectedMonth);
            console.log('Total Payroll Amount:', totalAmount);
            console.log('Employee Count:', attendanceEmployees.length);

            // Display employees with leaves
            console.log('\n EMPLOYEES WITH APPROVED LEAVES:');
            console.log('='.repeat(80));
            if (employeesWithLeaves.length > 0) {
                employeesWithLeaves.forEach(emp => {
                    console.log(`${emp.name}: ${emp.leaves} days leave, Net: ${emp.netSalary.toLocaleString()}`);
                });
            } else {
                console.log('No employees took approved leaves this month');
            }

            // Display employees with LOP
            console.log('\nEMPLOYEES WITH LOP (Loss of Pay):');
            console.log('='.repeat(80));
            if (employeesWithAttendanceIssues.length > 0) {
                employeesWithAttendanceIssues.forEach(emp => {
                    console.log(`${emp.name}: ${emp.lop} LOP days, Deduction: ${emp.lopDeduction.toLocaleString()}, Net: ${emp.netSalary.toLocaleString()}`);
                });
            } else {
                console.log('No employees have LOP this month');
            }

            setProcessingProgress(60);

            // BACKEND PERSISTENCE: Save payroll to backend
            try {
                // Process payroll via backend API
                await payrollService.processPayroll(selectedMonth, employeePayrollData);
                setProcessingProgress(80);

                // Bulk save payslips
                await payrollService.bulkSavePayslips(selectedMonth, employeePayrollData);
                setProcessingProgress(100);

                console.log('Payroll saved to backend successfully');
            } catch (backendError) {
                // Log warning but don't fail - fallback to local state
                console.warn('Backend save failed, using local state:', backendError.message);
                // Continue with local state update even if backend fails
            }

            // Update local state
            setCurrentStep('completed');
            setPayrollPeriods(payrollPeriods.map(p =>
                p.month === selectedMonth
                    ? { ...p, processed: true, payslipsGenerated: true, totalAmount: Math.round(totalAmount) }
                    : p
            ));

        } catch (error) {
            console.error('Error processing payroll:', error);
            setProcessingError(error.message || 'An error occurred while processing payroll');
            setCurrentStep('validation'); // Go back to validation on error
        }
    };

    const [lockingPayroll, setLockingPayroll] = useState(false);

    const lockPayroll = async () => {
        if (!window.confirm('Are you sure you want to lock this payroll? This action cannot be undone.')) {
            return;
        }

        setLockingPayroll(true);

        try {
            // Lock payroll via backend API
            await payrollService.lockPayroll(selectedMonth);
            console.log('Payroll locked successfully via backend');

            // Update local state
            setPayrollPeriods(payrollPeriods.map(p =>
                p.month === selectedMonth
                    ? { ...p, locked: true, status: 'locked' }
                    : p
            ));
        } catch (error) {
            console.warn('Backend lock failed, updating local state:', error.message);
            // Still update local state as fallback
            setPayrollPeriods(payrollPeriods.map(p =>
                p.month === selectedMonth
                    ? { ...p, locked: true, status: 'locked' }
                    : p
            ));
        } finally {
            setLockingPayroll(false);
        }
    };

    const viewPayslipDetail = (employee) => {
        setSelectedEmployee(employee);
        setShowPayslipDetail(true);
    };

    const getStatusBadge = (status) => {
        const badges = {
            locked: { bg: 'bg-gray-500', text: 'Locked', icon: Lock },
            current: { bg: 'bg-blue-500', text: 'Current', icon: Calendar },
            upcoming: { bg: 'bg-gray-400', text: 'Upcoming', icon: Calendar },
        };
        const badge = badges[status] || badges.upcoming;
        const Icon = badge.icon;
        return (
            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-white ${badge.bg}`}>
                <Icon className="w-3 h-3" />
                {badge.text}
            </div>
        );
    };

    const getValidationColor = (color) => {
        const colors = {
            blue: 'bg-blue-100 text-blue-700 border-blue-200',
            purple: 'bg-purple-100 text-purple-700 border-purple-200',
            pink: 'bg-pink-100 text-pink-700 border-pink-200',
            cyan: 'bg-cyan-100 text-cyan-700 border-cyan-200',
            orange: 'bg-orange-100 text-orange-700 border-orange-200',
            yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200',
        };
        return colors[color] || colors.blue;
    };

    /**
     * Calculate net salary for an employee (synchronous version)
     * NOTE: This is a local implementation for PayrollRun2 component.
     * For async version with backend PT calculation, use calculateNetSalaryAsync from salaryCalculations.js
     *
     * Key fixes applied:
     * - Uses calculatePF() with wage ceiling (₹15,000 cap) instead of direct 12% calculation
     * - Uses PAYROLL_CONFIG constants instead of hardcoded values
     * - Prevents division by zero in LOP calculation
     */
    const calculateNetSalary = (employee, attendanceData = null) => {
        // Get salary components from pay_package or salary_components and drop persisted PT
        const salaryComponents = (employee.salary_components || employee.pay_package?.salary_components || [])
            .filter(comp => (comp.name || '').toLowerCase() !== 'professional tax');

        // Extract monthly earnings from compensation structure
        let monthlyEarnings = {};
        let monthlyDeductions = {};

        if (salaryComponents.length > 0) {
            // Use actual compensation data
            salaryComponents.forEach(comp => {
                const monthlyAmount = comp.monthly_amount || (comp.amount / 12) || 0;
                if (comp.type === 'earning') {
                    monthlyEarnings[comp.name] = monthlyAmount;
                } else if (comp.type === 'deduction') {
                    monthlyDeductions[comp.name] = monthlyAmount;
                }
            });
        } else {
            // Fallback to CTC-based calculation using config
            const ctc = parseFloat(
                employee.ctc ||
                employee.annual_ctc ||
                employee.pay_package?.ctc ||
                employee.pay_package?.annual_ctc ||
                0
            );
            const monthlyCTC = ctc / 12;
            monthlyEarnings = {
                'Basic Salary': monthlyCTC * PAYROLL_CONFIG.DEFAULT_BASIC_SALARY_PERCENTAGE,
                'HRA (House Rent Allowance)': (monthlyCTC * PAYROLL_CONFIG.DEFAULT_BASIC_SALARY_PERCENTAGE) * PAYROLL_CONFIG.DEFAULT_HRA_PERCENTAGE,
                'Special Allowance': monthlyCTC * PAYROLL_CONFIG.DEFAULT_SPECIAL_ALLOWANCE_PERCENTAGE
            };
        }

        // Calculate total monthly earnings before LOP
        const fullMonthEarnings = Object.values(monthlyEarnings).reduce((sum, val) => sum + val, 0);

        // If attendance data is provided, calculate LOP deduction
        let lopDeduction = 0;
        let payableDays = PAYROLL_CONFIG.STANDARD_WORKING_DAYS_PER_MONTH; // Use config instead of hardcoded 22
        let proratedEarnings = { ...monthlyEarnings };

        if (attendanceData) {
            const workingDays = attendanceData.workingDays || PAYROLL_CONFIG.STANDARD_WORKING_DAYS_PER_MONTH;
            const lop = attendanceData.lop || 0;
            payableDays = workingDays - lop;

            // Calculate LOP factor and prorate each earning component
            const lopFactor = workingDays > 0 ? lop / workingDays : 0; // Prevent division by zero

            // Prorate each earning component
            Object.keys(proratedEarnings).forEach(key => {
                const fullAmount = monthlyEarnings[key];
                const deduction = fullAmount * lopFactor;
                proratedEarnings[key] = fullAmount - deduction;
                lopDeduction += deduction;
            });
        }

        // Calculate gross salary (sum of prorated earnings)
        const gross = Object.values(proratedEarnings).reduce((sum, val) => sum + val, 0);

        // Get basic salary for PF calculation
        const basicSalary = proratedEarnings['Basic Salary'] || (gross * PAYROLL_CONFIG.DEFAULT_BASIC_SALARY_PERCENTAGE);

        // FIX: Calculate PF using proper function with wage ceiling (₹15,000 cap)
        // Previously: const pf = basicSalary * 0.12 (no ceiling - caused over-deduction)
        // Now: Uses calculatePF which caps at PF_WAGE_CEILING (₹15,000)
        const pf = calculatePF(basicSalary, PAYROLL_CONFIG);
        const professionalTax = getProfessionalTax(employee?.state, gross, PAYROLL_CONFIG);
        const tax = 0; // Income tax will be calculated separately via TDS

        // Add any pre-defined deductions from compensation
        const otherDeductions = Object.values(monthlyDeductions).reduce((sum, val) => sum + val, 0);

        const totalDeductions = pf + professionalTax + tax + otherDeductions;
        const net = gross - totalDeductions;

        return {
            gross,
            pf,
            tax,
            professionalTax,
            otherDeductions,
            net,
            earningsBreakdown: proratedEarnings,
            deductionsBreakdown: {
                'Provident Fund (PF)': pf,
                ...(professionalTax > 0 ? { 'Professional Tax': professionalTax } : {}),
                ...monthlyDeductions
            },
            lopDeduction,
            payableDays,
            workingDays: attendanceData?.workingDays || PAYROLL_CONFIG.STANDARD_WORKING_DAYS_PER_MONTH,
            lop: attendanceData?.lop || 0
        };
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'validated': return 'text-green-600 bg-green-50';
            case 'pending': return 'text-yellow-600 bg-yellow-50';
            case 'issue': return 'text-red-600 bg-red-50';
            default: return 'text-gray-600 bg-gray-50';
        }
    };

    // Calculate summary stats from real data
    const calculateSummaryStats = () => {
        const attendanceEmployees = processEmployeeData(employees, 'attendance');
        const totalEmployees = employees.length;

        const totalGross = attendanceEmployees.reduce((sum, empData) => {
            const attendanceData = {
                workingDays: empData.workingDays,
                lop: empData.lop,
                leaves: empData.leaves
            };
            const salary = calculateNetSalary(empData.employee, attendanceData);
            return sum + salary.gross;
        }, 0);

        const totalDeductions = attendanceEmployees.reduce((sum, empData) => {
            const attendanceData = {
                workingDays: empData.workingDays,
                lop: empData.lop,
                leaves: empData.leaves
            };
            const salary = calculateNetSalary(empData.employee, attendanceData);
            return sum + salary.pf + salary.professionalTax;
        }, 0);

        const totalNet = totalGross - totalDeductions;

        return {
            totalEmployees,
            totalGross: Math.round(totalGross),
            totalDeductions: Math.round(totalDeductions),
            totalNet: Math.round(totalNet)
        };
    };

    const stats = calculateSummaryStats();

    return (
        <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
            <div className="p-6">
                {/* Header */}
                <div className="mb-6">
                    <h1 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Payroll Processing</h1>
                    <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Process monthly payroll with comprehensive validation</p>
                </div>

                {/* Month Selector */}
                <div className={`rounded-xl shadow-sm p-6 mb-6 ${
                    theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'
                }`}>
                    <div className="flex items-center justify-between mb-4">
                        <h2 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Select Payroll Period</h2>
                        <select
                            value={selectedMonth}
                            onChange={(e) => {
                                setSelectedMonth(e.target.value);
                                setCurrentStep('validation');
                            }}
                            className={`px-4 py-2 border rounded-lg ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                        >
                            {payrollPeriods.map((period) => (
                                <option key={period.month} value={period.month}>
                                    {period.month} - {period.processed ? 'Processed' : 'Not Processed'}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="grid grid-cols-6 gap-3">
                        {payrollPeriods.map((period, index) => (
                            <div
                                key={index}
                                onClick={() => {
                                    setSelectedMonth(period.month);
                                    setCurrentStep('validation');
                                }}
                                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                    selectedMonth === period.month
                                        ? theme === 'dark'
                                            ? 'border-blue-500 bg-blue-900/30'
                                            : 'border-blue-500 bg-blue-50'
                                        : period.locked
                                            ? theme === 'dark'
                                                ? 'border-gray-700 bg-gray-800/50 opacity-75'
                                                : 'border-gray-200 bg-gray-50 opacity-75'
                                            : theme === 'dark'
                                                ? 'border-gray-700 hover:border-blue-500'
                                                : 'border-gray-200 hover:border-blue-300'
                                }`}
                            >
                                <div className="text-center">
                                    <p className={`text-xs font-semibold mb-1 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                        {period.month.split(' ')[0]}
                                    </p>
                                    <p className={`text-[10px] mb-2 ${
                                        theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                                    }`}>{period.range}</p>
                                    {getStatusBadge(period.status)}
                                    {period.processed && (
                                        <div className="mt-2 flex items-center justify-center gap-1">
                                            <CheckCircle className="w-3 h-3 text-green-600" />
                                            <span className="text-[9px] text-green-600 font-medium">Processed</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {selectedPeriod && (
                    <>
                        {/* Period Summary */}
                        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl p-6 mb-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="text-2xl font-bold">{selectedMonth}</h2>
                                    <p className="text-sm opacity-90 mt-1">{selectedPeriod.range}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm opacity-90">Total Employees</p>
                                    <p className="text-xl font-bold">{employees.length}</p>
                                </div>
                            </div>
                        </div>

                        {/* Validation Workflow */}
                        {!selectedPeriod.processed ? (
                            <div className={`rounded-xl shadow-sm p-6 mb-6 ${
                                theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'
                            }`}>
                                <div className="mb-6">
                                    <h2 className={`text-xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Payroll Validation</h2>
                                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Complete all validation steps before processing payroll</p>

                                    {/* Progress Bar */}
                                    <div className={`mt-4 p-4 rounded-lg ${
                                        theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
                                    }`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <span className={`text-sm font-medium ${
                                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                            }`}>Validation Progress</span>
                                            <span className={`text-sm font-semibold ${
                                                theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                                            }`}>
                                                {validations.filter(v => v.validated).length} of {validations.length} completed
                                            </span>
                                        </div>
                                        <div className={`h-2.5 rounded-full overflow-hidden ${
                                            theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'
                                        }`}>
                                            <div
                                                className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500"
                                                style={{ width: `${(validations.filter(v => v.validated).length / validations.length) * 100}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Validation Categories - Card View */}
                                <div className="grid grid-cols-2 gap-4">
                                    {validations.map((validation) => {
                                        const Icon = validation.icon;
                                        const progress = validation.totalEmployees > 0 ? (validation.validatedCount / validation.totalEmployees) * 100 : 0;

                                        return (
                                            <div
                                                key={validation.id}
                                                className={`border-2 rounded-lg p-5 transition-all cursor-pointer hover:shadow-md ${
                                                    validation.validated
                                                        ? theme === 'dark'
                                                            ? 'border-green-600 bg-green-900/30'
                                                            : 'border-green-500 bg-green-50'
                                                        : theme === 'dark'
                                                            ? 'border-gray-700 hover:border-blue-500'
                                                            : 'border-gray-200 hover:border-blue-300'
                                                }`}
                                                onClick={() => openValidationDetail(validation)}
                                            >
                                                <div className="flex items-start justify-between mb-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`p-2 rounded-lg ${getValidationColor(validation.color)}`}>
                                                            <Icon className="w-5 h-5" />
                                                        </div>
                                                        <div>
                                                            <h3 className={`font-semibold text-sm ${
                                                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                            }`}>{validation.title}</h3>
                                                            <p className={`text-xs mt-0.5 ${
                                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                                            }`}>{validation.totalEmployees} employees</p>
                                                        </div>
                                                    </div>
                                                    {validation.validated && (
                                                        <CheckCircle className="w-6 h-6 text-green-600" />
                                                    )}
                                                </div>

                                                {/* Stats */}
                                                <div className="grid grid-cols-3 gap-3 mb-3">
                                                    <div className="text-center p-2 bg-green-50 rounded">
                                                        <p className="text-xl font-bold text-green-600">{validation.validatedCount}</p>
                                                        <p className="text-[10px] text-gray-600">Validated</p>
                                                    </div>
                                                    <div className="text-center p-2 bg-yellow-50 rounded">
                                                        <p className="text-xl font-bold text-yellow-600">{validation.pendingCount}</p>
                                                        <p className="text-[10px] text-gray-600">Pending</p>
                                                    </div>
                                                    <div className="text-center p-2 bg-red-50 rounded">
                                                        <p className="text-xl font-bold text-red-600">{validation.issuesCount}</p>
                                                        <p className="text-[10px] text-gray-600">Issues</p>
                                                    </div>
                                                </div>

                                                {/* Progress */}
                                                <div className="mb-3">
                                                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                                                        <span>Progress</span>
                                                        <span>{Math.round(progress)}%</span>
                                                    </div>
                                                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all"
                                                            style={{ width: `${progress}%` }}
                                                        />
                                                    </div>
                                                </div>

                                                <button className="w-full py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                                                    View Details →
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Process Payroll Button */}
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-gray-600">
                                                {allValidated
                                                    ? 'All validations complete. Ready to process payroll.'
                                                    : 'Complete all validations to process payroll'}
                                            </p>
                                        </div>
                                        <button
                                            onClick={processPayroll}
                                            disabled={!allValidated}
                                            className={`px-8 py-3 rounded-lg font-semibold transition-colors ${
                                                allValidated
                                                    ? 'bg-green-600 text-white hover:bg-green-700'
                                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            }`}
                                        >
                                            Process Payroll
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* Processed Payroll View */
                            <div className="bg-white rounded-xl shadow-sm p-6">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-3 bg-green-100 rounded-lg">
                                        <CheckCircle className="w-8 h-8 text-green-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900">Payroll Completed</h2>
                                        <p className="text-sm text-gray-600">Payslips have been generated for all employees</p>
                                    </div>
                                </div>

                                {/* Payroll Summary */}
                                <div className="grid grid-cols-4 gap-6 mb-6 p-6 bg-gray-50 rounded-lg">
                                    <div>
                                        <p className="text-xs text-gray-500 mb-1">Total Employees</p>
                                        <p className="text-2xl font-bold text-gray-900">{stats.totalEmployees}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 mb-1">Gross Amount</p>
                                        <p className="text-2xl font-bold text-gray-900">₹{stats.totalGross.toLocaleString()}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 mb-1">Deductions</p>
                                        <p className="text-2xl font-bold text-red-600">₹{stats.totalDeductions.toLocaleString()}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 mb-1">Net Payable</p>
                                        <p className="text-2xl font-bold text-green-600">₹{stats.totalNet.toLocaleString()}</p>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setShowPayslipModal(true)}
                                        className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 flex items-center justify-center gap-2"
                                    >
                                        <Eye className="w-5 h-5" />
                                        View Payslips
                                    </button>
                                    <button className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 flex items-center justify-center gap-2">
                                        <Download className="w-5 h-5" />
                                        Download All
                                    </button>
                                    <button
                                        onClick={lockPayroll}
                                        disabled={selectedPeriod.locked}
                                        className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 ${
                                            selectedPeriod.locked
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                : 'bg-orange-600 text-white hover:bg-orange-700'
                                        }`}
                                    >
                                        <Lock className="w-5 h-5" />
                                        {selectedPeriod.locked ? 'Locked' : 'Lock Payroll'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Attendance Validation Wizard */}
                {showValidationDetail && selectedValidation && selectedValidation.id === 'attendance' && (
                    <AttendanceValidationWizard
                        validation={selectedValidation}
                        onClose={() => setShowValidationDetail(false)}
                        onComplete={() => validateCategory('attendance')}
                        theme={theme}
                    />
                )}

                {/* Regular Validation Detail Modal (for non-attendance validations) */}
                {showValidationDetail && selectedValidation && selectedValidation.id !== 'attendance' && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
                            {/* Header */}
                            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => setShowValidationDetail(false)}
                                        className="p-2 hover:bg-gray-100 rounded-lg"
                                    >
                                        <ArrowLeft className="w-5 h-5" />
                                    </button>
                                    <div>
                                        <h2 className="text-2xl font-bold text-gray-900">{selectedValidation.title}</h2>
                                        <p className="text-sm text-gray-600">{selectedValidation.totalEmployees} total employees</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowValidationDetail(false)}
                                    className="p-2 hover:bg-gray-100 rounded-lg"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Stats Bar */}
                            <div className="p-4 bg-gray-50 border-b border-gray-200 grid grid-cols-4 gap-4">
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-gray-900">{selectedValidation.totalEmployees}</p>
                                    <p className="text-xs text-gray-600">Total</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-green-600">{selectedValidation.validatedCount}</p>
                                    <p className="text-xs text-gray-600">Validated</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-yellow-600">{selectedValidation.pendingCount}</p>
                                    <p className="text-xs text-gray-600">Pending</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-red-600">{selectedValidation.issuesCount}</p>
                                    <p className="text-xs text-gray-600">Issues</p>
                                </div>
                            </div>

                            {/* Toolbar */}
                            <div className="p-4 border-b border-gray-200 flex items-center gap-3">
                                <div className="flex-1 relative">
                                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search by name or employee ID..."
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                                    />
                                </div>
                                <select
                                    value={filterStatus}
                                    onChange={(e) => {
                                        setFilterStatus(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="px-4 py-2 border border-gray-300 rounded-lg"
                                >
                                    <option value="all">All Status</option>
                                    <option value="validated">Validated</option>
                                    <option value="pending">Pending</option>
                                    <option value="issue">Issues</option>
                                </select>
                                <button
                                    onClick={selectAllFiltered}
                                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    Select All
                                </button>
                                <button
                                    onClick={bulkValidate}
                                    disabled={selectedEmployees.length === 0}
                                    className={`px-4 py-2 rounded-lg font-medium ${
                                        selectedEmployees.length > 0
                                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    }`}
                                >
                                    Validate Selected ({selectedEmployees.length})
                                </button>
                            </div>

                            {/* Employee List */}
                            <div className="flex-1 overflow-y-auto p-4">
                                <div className="space-y-2">
                                    {getPaginatedEmployees().map((employee) => (
                                        <div key={employee.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={selectedEmployees.includes(employee.id)}
                                                onChange={() => toggleEmployeeSelection(employee.id)}
                                                className="w-4 h-4"
                                            />
                                            <div className="flex-1 grid grid-cols-5 gap-4 items-center">
                                                <div>
                                                    <p className="font-medium text-gray-900">{employee.name}</p>
                                                    <p className="text-xs text-gray-500">ID: {employee.id}</p>
                                                    {employee.isProrated && (
                                                        <span className="inline-block mt-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-medium rounded">
                                                            {employee.joinerType === 'joiner' ? '📥 New Joiner' : employee.joinerType === 'exit' ? '📤 Exit' : '📥📤 Join+Exit'}
                                                        </span>
                                                    )}
                                                    {employee.exitRequestStatus === 'pending_approval' && (
                                                        <span className="inline-block mt-1 ml-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-[10px] font-medium rounded">
                                                            ⏳ Pending Approval
                                                        </span>
                                                    )}
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-600">{employee.department}</p>
                                                    {employee.isProrated && employee.joiningDate && employee.joinerType === 'joiner' && (
                                                        <p className="text-xs text-purple-600">
                                                            Joined: {employee.joiningDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                        </p>
                                                    )}
                                                    {/* Exit date with override capability */}
                                                    {(employee.joinerType === 'exit' || employee.joinerType === 'joiner-exit') && employee.exitDate && (
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <span className="text-xs text-gray-500">Last Working Day:</span>
                                                            <input
                                                                type="date"
                                                                value={exitDateOverrides[employee.id] || (employee.exitDate instanceof Date ? employee.exitDate.toISOString().split('T')[0] : employee.exitDate?.split('T')[0]) || ''}
                                                                onChange={(e) => handleExitDateOverride(employee.id, e.target.value)}
                                                                className="px-2 py-0.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                            />
                                                            {employee.isExitDateOverridden && (
                                                                <button
                                                                    onClick={() => handleExitDateOverride(employee.id, null)}
                                                                    className="text-xs text-red-500 hover:text-red-700"
                                                                    title="Reset to original date"
                                                                >
                                                                    ✕
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-600">WD: {employee.workingDays} | LOP: {employee.lop}</p>
                                                    {employee.isProrated && (
                                                        <p className="text-xs text-purple-600">{employee.effectiveDays} days worked</p>
                                                    )}
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-600">Leaves: {employee.leaves} | OT: {employee.overtime}h</p>
                                                    {employee.isProrated && (
                                                        <p className="text-xs text-orange-600">Pro-rated salary</p>
                                                    )}
                                                    {employee.isExitDateOverridden && (
                                                        <p className="text-xs text-blue-600 font-medium">Exit date overridden</p>
                                                    )}
                                                </div>
                                                <div className="flex items-center justify-end gap-2">
                                                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(employee.status)}`}>
                                                        {employee.status}
                                                    </span>
                                                    <button className="p-1.5 text-blue-600 hover:bg-blue-50 rounded">
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Pagination */}
                            <div className="p-4 border-t border-gray-200 flex items-center justify-between">
                                <p className="text-sm text-gray-600">
                                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, getFilteredEmployees().length)} of {getFilteredEmployees().length} employees
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className={`p-2 rounded-lg ${
                                            currentPage === 1
                                                ? 'text-gray-400 cursor-not-allowed'
                                                : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                    >
                                        <ChevronLeft className="w-5 h-5" />
                                    </button>
                                    <span className="px-4 py-2 text-sm font-medium">
                                        Page {currentPage} of {getTotalPages()}
                                    </span>
                                    <button
                                        onClick={() => setCurrentPage(p => Math.min(getTotalPages(), p + 1))}
                                        disabled={currentPage === getTotalPages()}
                                        className={`p-2 rounded-lg ${
                                            currentPage === getTotalPages()
                                                ? 'text-gray-400 cursor-not-allowed'
                                                : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                    >
                                        <ChevronRight className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Footer Actions */}
                            <div className="p-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                                <button
                                    onClick={() => setShowValidationDetail(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={() => {
                                        validateCategory(selectedValidation.id);
                                        setShowValidationDetail(false);
                                    }}
                                    className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
                                >
                                    Mark All as Validated
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Processing Modal */}
                {currentStep === 'processing' && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
                        <div className={`rounded-xl p-8 max-w-md w-full text-center ${
                            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                        }`}>
                            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                            <h3 className={`text-xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Processing Payroll</h3>
                            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Calculating salaries and generating payslips...</p>
                        </div>
                    </div>
                )}

                {/* Prerequisite Check Modal */}
                {showPrerequisiteModal && prerequisiteIssues.length > 0 && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className={`rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
                            theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'
                        }`}>
                            {/* Modal Header */}
                            <div className={`p-6 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                                <div className="flex items-start gap-4">
                                    <div className={`p-3 rounded-full ${
                                        theme === 'dark' ? 'bg-yellow-900/30' : 'bg-yellow-100'
                                    }`}>
                                        <AlertCircle className={`w-8 h-8 ${
                                            theme === 'dark' ? 'text-yellow-500' : 'text-yellow-600'
                                        }`} />
                                    </div>
                                    <div className="flex-1">
                                        <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                            Setup Required
                                        </h2>
                                        <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                            Please complete the following setup steps before running payroll
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setShowPrerequisiteModal(false)}
                                        className={`p-2 rounded-lg transition-colors ${
                                            theme === 'dark' ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                                        }`}
                                    >
                                        <XCircle className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6 space-y-4">
                                {prerequisiteIssues.map((issue, index) => (
                                    <div
                                        key={index}
                                        className={`p-5 rounded-lg border ${
                                            theme === 'dark'
                                                ? 'bg-gray-700/30 border-gray-600'
                                                : 'bg-gray-50 border-gray-200'
                                        }`}
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className={`p-2 rounded-lg ${
                                                theme === 'dark' ? 'bg-red-900/30' : 'bg-red-50'
                                            }`}>
                                                <XCircle className={`w-5 h-5 ${
                                                    theme === 'dark' ? 'text-red-500' : 'text-red-600'
                                                }`} />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className={`font-semibold mb-1 ${
                                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                }`}>
                                                    {issue.title}
                                                </h3>
                                                <p className={`text-sm mb-3 ${
                                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                }`}>
                                                    {issue.description}
                                                </p>
                                                <button
                                                    onClick={() => {
                                                        setShowPrerequisiteModal(false);
                                                        navigate(issue.route);
                                                    }}
                                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                                                >
                                                    {issue.action}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Modal Footer */}
                            <div className={`p-6 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                                <div className="flex items-center justify-between">
                                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                        Complete all requirements to enable payroll processing
                                    </p>
                                    <button
                                        onClick={() => setShowPrerequisiteModal(false)}
                                        className={`px-4 py-2 rounded-lg border transition-colors ${
                                            theme === 'dark'
                                                ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                        }`}
                                    >
                                        I'll do this later
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Payslip List Modal */}
                {showPayslipModal && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className={`rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col ${
                            theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'
                        }`}>
                            {/* Header */}
                            <div className={`p-6 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                            Employee Payslips - {selectedMonth}
                                        </h2>
                                        <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                            Click on any employee to view their payslip
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setShowPayslipModal(false)}
                                        className={`p-2 rounded-lg transition-colors ${
                                            theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                                        }`}
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Employee List */}
                            <div className="flex-1 overflow-y-auto p-6">
                                <div className="grid grid-cols-1 gap-3">
                                    {employees.map((employee) => {
                                        // Get attendance data for this employee
                                        const attendanceEmployees = processEmployeeData(employees, 'attendance');
                                        const empAttendanceData = attendanceEmployees.find(e => e.id === employee.id);
                                        const attendanceData = empAttendanceData ? {
                                            workingDays: empAttendanceData.workingDays,
                                            lop: empAttendanceData.lop,
                                            leaves: empAttendanceData.leaves,
                                            basicSalary: empAttendanceData.basicSalary
                                        } : null;
                                        const salary = calculateNetSalary(employee, attendanceData);
                                        return (
                                            <div
                                                key={employee.id}
                                                onClick={() => {
                                                    setSelectedEmployee(employee);
                                                    setShowPayslipDetail(true);
                                                    setShowPayslipModal(false);
                                                }}
                                                className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                                                    theme === 'dark'
                                                        ? 'bg-gray-700/30 border-gray-600 hover:border-blue-500'
                                                        : 'bg-gray-50 border-gray-200 hover:border-blue-500'
                                                }`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-4">
                                                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                                            theme === 'dark' ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-600'
                                                        }`}>
                                                            <Users className="w-6 h-6" />
                                                        </div>
                                                        <div>
                                                            <h3 className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                                {employee.name || `${employee.first_name} ${employee.last_name}`}
                                                            </h3>
                                                            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                                                {employee.id || employee.employee_code} • {employee.department}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                            ₹{Math.round(salary.net).toLocaleString()}
                                                        </p>
                                                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                                            Net Salary
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Footer */}
                            <div className={`p-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                                <div className="flex items-center justify-between">
                                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                        Total Employees: {employees.length}
                                    </p>
                                    <button
                                        onClick={() => setShowPayslipModal(false)}
                                        className={`px-4 py-2 rounded-lg border transition-colors ${
                                            theme === 'dark'
                                                ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                        }`}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Individual Payslip Modal */}
                {showPayslipDetail && selectedEmployee && (() => {
                    // Get attendance data for selected employee
                    const attendanceEmployees = processEmployeeData(employees, 'attendance');
                    const empAttendanceData = attendanceEmployees.find(e => e.id === selectedEmployee.id);

                    // Get the full employee object from employees array to ensure we have all data
                    const fullEmployee = employees.find(e => e.id === selectedEmployee.id) || selectedEmployee;

                    console.log('🔍 Payslip - Full Employee Data:', fullEmployee);
                    console.log('📊 Payslip - Attendance Data:', empAttendanceData);
                    console.log('💰 Payslip - Salary Components:', fullEmployee.salary_components);
                    console.log('📦 Payslip - Pay Package:', fullEmployee.pay_package);

                    // Create enhanced employee object with attendance data AND salary components
                    const enhancedEmployee = {
                        ...fullEmployee, // Use full employee data (includes salary_components, pay_package, ctc, etc.)
                        workingDays: empAttendanceData?.workingDays || 22,
                        lop: empAttendanceData?.lop || 0,
                        leaves: empAttendanceData?.leaves || 0,
                        basicSalary: empAttendanceData?.basicSalary,
                        partialDays: empAttendanceData?.partialDays || [],
                        isProrated: empAttendanceData?.isProrated || false
                    };

                    console.log('✅ Payslip - Enhanced Employee:', enhancedEmployee);

                    return (
                        <Payslip
                            employee={enhancedEmployee}
                            payrollMonth={selectedMonth}
                            onClose={() => {
                                setShowPayslipDetail(false);
                                setSelectedEmployee(null);
                                setShowPayslipModal(true); // Go back to list
                            }}
                        />
                    );
                })()}
            </div>
        </div>
    );
};

export default PayrollRun;
